import React, { useState } from "react";
import { KeyRound, LogOut, Save, AlertCircle, CheckCircle2, User } from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";

export const AdminAccount = () => {
  const { admin, logout, changePassword } = useAdminAuth();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (next1.length < 8) {
      setError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (next1 !== next2) {
      setError("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next1);
      setSaved(true);
      setCurrent("");
      setNext1("");
      setNext2("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Mise à jour impossible.");
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, value, onChange, testId, autoComplete }) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
        {label}
      </span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testId}
        className="mt-1 w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2 px-3 text-sm text-white focus:border-[#FF9500] focus:outline-none transition-colors"
      />
    </label>
  );

  return (
    <div data-testid="admin-page-account" className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FF9500] font-street inline-flex items-center gap-2">
          <User size={12} /> Compte administrateur
        </div>
        <h1 className="font-street mt-2 text-3xl tracking-wide">Mon compte</h1>
      </header>

      <section className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6 max-w-xl">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
          Email
        </div>
        <div className="font-display text-xl text-white mt-1">{admin?.email || "—"}</div>
        <div className="mt-1 text-[11px] text-white/45">Rôle : {admin?.role || "admin"}</div>

        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout"
          className="mt-4 font-street inline-flex items-center gap-2 rounded-md border border-[#DC2626]/40 bg-[#DC2626]/10 px-4 py-2 text-xs tracking-wider text-[#FCA5A5] hover:bg-[#DC2626]/20 transition-colors"
        >
          <LogOut size={13} /> Se déconnecter
        </button>
      </section>

      <form onSubmit={onSubmit} className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6 max-w-xl space-y-4">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FF9500] font-street inline-flex items-center gap-2">
          <KeyRound size={12} /> Sécurité
        </div>
        <h2 className="font-street text-xl tracking-wide">Changer mon mot de passe</h2>

        <Field
          label="Mot de passe actuel"
          value={current}
          onChange={setCurrent}
          testId="admin-account-current-pwd"
          autoComplete="current-password"
        />
        <Field
          label="Nouveau mot de passe (min. 8 caractères)"
          value={next1}
          onChange={setNext1}
          testId="admin-account-new-pwd"
          autoComplete="new-password"
        />
        <Field
          label="Confirmer le nouveau mot de passe"
          value={next2}
          onChange={setNext2}
          testId="admin-account-confirm-pwd"
          autoComplete="new-password"
        />

        {error && (
          <div
            role="alert"
            data-testid="admin-account-error"
            className="inline-flex items-start gap-2 rounded-md border border-[#DC2626]/40 bg-[#DC2626]/10 px-3 py-2 text-[12px] text-[#FCA5A5] leading-snug w-full"
          >
            <AlertCircle size={13} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {saved && (
          <div
            role="status"
            data-testid="admin-account-saved"
            className="inline-flex items-center gap-2 rounded-md border border-[#22C55E]/40 bg-[#22C55E]/10 px-3 py-2 text-[12px] text-[#86EFAC]"
          >
            <CheckCircle2 size={13} /> Mot de passe mis à jour.
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-account-submit"
          className="font-street inline-flex items-center gap-2 rounded-md bg-[#FF9500] px-5 py-2.5 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 disabled:opacity-60 transition-all"
        >
          <Save size={14} /> {loading ? "Enregistrement…" : "Mettre à jour"}
        </button>
      </form>
    </div>
  );
};

export default AdminAccount;
